'use client'

import {
   Badge,
   Box,
   Card,
   Container,
   Stack,
   Text,
   ThemeIcon
} from '@blocks'
import {
   siteFeatureCardBackgroundHex,
   siteFeatureCardBodyHex,
   siteFeatureCardMutedLabelHex,
   siteFeatureCardShadow,
   siteSectionCardRadiusPx,
   siteTextOnDarkBandHex,
   siteTextOnDarkBandMutedHex,
   siteTypographySectionLeadFontSizeDesktop,
   siteTypographySectionLeadFontSizeMobile,
   siteTypographySectionTitlePrimaryFontSizeDesktop,
   siteTypographySectionTitlePrimaryFontSizeMobile,
   siteTypographySectionTitleSecondaryFontSizeDesktop,
   siteTypographySectionTitleSecondaryFontSizeMobile
} from '@constants'
import {useMediaQuery} from '@hooks'
import {IconRuler} from '@icons'
import {FaqInSection} from '@/blocks/site/FaqAccordion'

const washHeights = [
   {
      height: 'Auto Wash 1 — 2.8m',
      label: 'Extended Height',
      description:
         'Accommodates SUVs, utes, vans, and taller vehicles that need extra clearance.'
   },
   {
      height: 'Auto Wash 2 — 2.3m',
      label: 'Standard Height',
      description:
         'Suitable for standard cars, sedans, hatchbacks, and most everyday vehicles.'
   }
]

export const AutomaticCarWash = () => {
   const isMobile = useMediaQuery('(max-width: 768px)')

   return (
      <Box bg={'primary.9'}>
         <Container
            id={'automatic'}
            py={isMobile ? 80 : 120}
            size={'xl'}
            style={{scrollMarginTop: 120}}
         >
            <Stack gap={isMobile ? 50 : 70}>
               <Stack
                  gap={50}
                  style={{
                     display: 'grid',
                     gridTemplateColumns:
                        'repeat(auto-fit, minmax(340px, 1fr))',
                     alignItems: 'center'
                  }}
               >
                  <Stack gap={30}>
                     <Stack gap={12}>
                        <Badge
                           size={'lg'}
                           fw={700}
                           tt={'uppercase'}
                           styles={{
                              root: {
                                 backgroundColor:
                                    'rgba(255,255,255,0.12)',
                                 color: 'white',
                                 border:
                                    '1px solid rgba(255,255,255,0.22)'
                              }
                           }}
                        >
                           Automatic Car Wash
                        </Badge>
                        <Text
                           c={siteTextOnDarkBandHex}
                           fz={
                              isMobile
                                 ? siteTypographySectionTitlePrimaryFontSizeMobile
                                 : siteTypographySectionTitlePrimaryFontSizeDesktop
                           }
                           fw={800}
                           lh={1.05}
                        >
                           Fast. Reliable. Thorough.
                        </Text>
                     </Stack>
                     <Stack gap={18}>
                        <Text
                           c={siteTextOnDarkBandMutedHex}
                           fz={
                              isMobile
                                 ? siteTypographySectionLeadFontSizeMobile
                                 : siteTypographySectionLeadFontSizeDesktop
                           }
                           fw={500}
                           lh={1.7}
                        >
                           Get a fast, reliable wash with our two
                           automatic wash machines, designed to suit a
                           range of vehicle sizes.
                        </Text>
                        <Text
                           c={siteTextOnDarkBandMutedHex}
                           fz={
                              isMobile
                                 ? siteTypographySectionLeadFontSizeMobile
                                 : siteTypographySectionLeadFontSizeDesktop
                           }
                           fw={500}
                           lh={1.7}
                        >
                           With wash heights of 2.8m (Auto Wash 1) and
                           2.3m (Auto Wash 2), Clean Park can
                           accommodate standard vehicles as well as
                           taller vehicles that need extra clearance.
                           Whether you are after a quick clean or
                           regular maintenance, our automatic wash
                           delivers a thorough result with minimal
                           hassle.
                        </Text>
                     </Stack>
                  </Stack>
                  <Stack gap={20}>
                     {washHeights.map((washHeight) => (
                        <Card
                           key={washHeight.height}
                           p={isMobile ? 32 : 40}
                           radius={siteSectionCardRadiusPx}
                           style={{
                              background:
                                 siteFeatureCardBackgroundHex,
                              boxShadow: siteFeatureCardShadow
                           }}
                        >
                           <Stack gap={10}>
                              <ThemeIcon
                                 p={10}
                                 size={52}
                                 color={'white'}
                                 variant={'light'}
                                 radius={'lg'}
                              >
                                 <IconRuler
                                    stroke={1.6}
                                    size={30}
                                 />
                              </ThemeIcon>
                              <Text
                                 c={'white'}
                                 fz={
                                    isMobile
                                       ? siteTypographySectionTitleSecondaryFontSizeMobile
                                       : siteTypographySectionTitleSecondaryFontSizeDesktop
                                 }
                                 fw={800}
                                 lh={1}
                              >
                                 {washHeight.height}
                              </Text>
                              <Text
                                 c={siteFeatureCardMutedLabelHex}
                                 fz={11}
                                 fw={700}
                                 tt={'uppercase'}
                                 lts={1.5}
                              >
                                 {washHeight.label}
                              </Text>
                              <Text
                                 c={siteFeatureCardBodyHex}
                                 fz={
                                    isMobile
                                       ? siteTypographySectionLeadFontSizeMobile
                                       : siteTypographySectionLeadFontSizeDesktop
                                 }
                                 fw={500}
                                 lh={1.6}
                              >
                                 {washHeight.description}
                              </Text>
                           </Stack>
                        </Card>
                     ))}
                  </Stack>
               </Stack>
               <FaqInSection
                  categoryPriority={'car-wash'}
                  sectionTone={'dark'}
               />
            </Stack>
         </Container>
      </Box>
   )
}
